/**
 * useLoadingState Hook
 *
 * Coordinates the loading animation with app state.
 * Features:
 * - Tracks auth/data loading progress
 * - Manages animation phases (intro, loop, exit, complete)
 * - Enforces minimum display time for smooth UX
 * - Coordinates exit animation timing
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { AnimationPhase, LoadingState as LoadingStateType } from '../types';

interface UseLoadingStateOptions {
  /** Minimum time to show the loader in ms (default: 1500) */
  minDisplayTime?: number;

  /** Duration of exit animation in ms (default: 800) */
  exitDuration?: number;

  /** Whether the app data is ready (e.g., auth loaded) */
  isReady?: boolean;

  /** Whether there's an error state */
  hasError?: boolean;

  /** Error message if hasError is true */
  errorMessage?: string;
}

interface UseLoadingStateReturn {
  /** Current loading state */
  state: LoadingStateType;

  /** Current animation phase */
  phase: AnimationPhase;

  /** Whether the loader should be visible */
  isVisible: boolean;

  /** Whether the loader has completed and can be removed */
  isComplete: boolean;

  /** Manually trigger the exit animation */
  triggerExit: () => void;

  /** Set the animation phase (called by animation engine) */
  setPhase: (phase: AnimationPhase) => void;

  /** Callback when animation is complete */
  onComplete: () => void;
}

export function useLoadingState(options: UseLoadingStateOptions = {}): UseLoadingStateReturn {
  const {
    minDisplayTime = 1500,
    // exitDuration kept in options for potential future use
    isReady = false,
    hasError = false,
    errorMessage,
  } = options;

  // State
  const [phase, setPhase] = useState<AnimationPhase>('intro');
  const [isComplete, setIsComplete] = useState(false);

  // Timing refs
  const startTimeRef = useRef<number>(Date.now());
  const hasTriggeredExitRef = useRef(false);
  const exitTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup exit timeout on unmount
  useEffect(() => {
    return () => {
      if (exitTimeoutRef.current) {
        clearTimeout(exitTimeoutRef.current);
      }
    };
  }, []);

  // Check if minimum display time has passed
  const hasMinTimePassed = useCallback((): boolean => {
    return Date.now() - startTimeRef.current >= minDisplayTime;
  }, [minDisplayTime]);

  // Trigger exit animation
  const triggerExit = useCallback(() => {
    if (hasTriggeredExitRef.current) return;

    if (hasMinTimePassed()) {
      hasTriggeredExitRef.current = true;
      setPhase('exit');
    } else {
      // Wait for minimum time then trigger exit
      const remaining = minDisplayTime - (Date.now() - startTimeRef.current);
      exitTimeoutRef.current = setTimeout(() => {
        hasTriggeredExitRef.current = true;
        setPhase('exit');
      }, remaining);
    }
  }, [hasMinTimePassed, minDisplayTime]);

  // Handle phase changes from animation engine
  const handleSetPhase = useCallback((newPhase: AnimationPhase) => {
    setPhase(newPhase);

    if (newPhase === 'complete') {
      setIsComplete(true);
    }
  }, []);

  // Handle animation complete
  const handleComplete = useCallback(() => {
    setIsComplete(true);
    setPhase('complete');
  }, []);

  // Effect to trigger exit when ready and min time passed
  useEffect(() => {
    if (isReady && phase === 'loop' && !hasTriggeredExitRef.current) {
      triggerExit();
    }
  }, [isReady, phase, triggerExit]);

  // Effect to check if we should trigger exit after intro
  useEffect(() => {
    if (phase === 'intro' && isReady) {
      // Will trigger exit once we reach loop phase
    }
  }, [phase, isReady]);

  // Build loading state object
  const exitOrCompleteProgress = phase === 'exit' ? 0.5 : 1;
  const loopOrLaterProgress = phase === 'loop' ? 1 : exitOrCompleteProgress;
  const progress = phase === 'intro' ? 0.5 : loopOrLaterProgress;

  const state: LoadingStateType = {
    phase,
    progress,
    isReady,
    hasError,
    errorMessage,
  };

  return {
    state,
    phase,
    isVisible: !isComplete,
    isComplete,
    triggerExit,
    setPhase: handleSetPhase,
    onComplete: handleComplete,
  };
}

/**
 * Hook to detect prefers-reduced-motion
 */
export function usePrefersReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    const handler = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    }
    // Legacy browsers
    mediaQuery.addListener(handler);
    return () => mediaQuery.removeListener(handler);
  }, []);

  return prefersReducedMotion;
}

/**
 * Hook to hide the static HTML preloader
 */
export function useHideHtmlPreloader(isComplete: boolean): void {
  useEffect(() => {
    if (!isComplete) return;

    let hideTimeout: NodeJS.Timeout | null = null;
    const preloader = document.getElementById('spirit-preloader');
    if (preloader) {
      preloader.classList.add('exit');

      // Remove from DOM after transition
      hideTimeout = setTimeout(() => {
        preloader.classList.add('hidden');
      }, 600);
    }

    return () => {
      if (hideTimeout) {
        clearTimeout(hideTimeout);
      }
    };
  }, [isComplete]);
}
